<script>
  import { useRoute } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';

  const { route, location } = useRoute();
  const conversationId = $derived(Number(location?.params?.id ?? route?.params?.id));

  let loading = $state(true);
  let error = $state(null);
  let thread = $state(null);

  $effect(() => {
    if (!conversationId) return;
    loading = true;
    error = null;
    (async () => {
      try {
        thread = await api.getConversation(conversationId, {
          tenant_id: authStore.tenantId ?? undefined,
        });
      } catch (e) {
        error = e.status === 404
          ? 'This conversation is not available for your account.'
          : e.message;
      } finally {
        loading = false;
      }
    })();
  });

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

  /** Auto-responses keyed by the message they were generated against. */
  const responsesByMessage = $derived.by(() => {
    const map = new Map();
    for (const r of thread?.auto_responses ?? []) {
      if (r.original_message_id == null) continue;
      if (!map.has(r.original_message_id)) map.set(r.original_message_id, []);
      map.get(r.original_message_id).push(r);
    }
    return map;
  });

  function outcomeLabel(r) {
    if (r.response_type === 'auto') return 'Sent automatically';
    if (r.human_reviewed) return r.human_approved ? 'Approved by host' : 'Rejected by host';
    return r.response_type === 'escalated' ? 'Escalated to host' : 'Awaiting host review';
  }

  function outcomeTone(r) {
    if (r.response_type === 'auto') return 'var(--p-success)';
    if (!r.human_reviewed) return 'var(--p-warning)';
    return r.human_approved ? 'var(--p-success)' : 'var(--p-text-muted)';
  }
</script>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if loading}
  <p class="p-muted text-sm">Loading…</p>
{:else if thread}
  <PageHeader title={thread.conversation.guest_name ?? 'Conversation'}
              subtitle={[thread.conversation.property_name ?? 'Unlinked property',
                         thread.conversation.channel_name].filter(Boolean).join(' · ')} />

  {#if thread.conversation.is_unlinked && authStore.canViewUnlinked}
    <p class="mb-4 rounded-lg px-3 py-2 text-xs"
       style="background-color: var(--p-warning-bg); color: var(--p-warning)">
      <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
      No property is linked to this thread, so property knowledge could not be scoped correctly.
    </p>
  {/if}

  <div class="space-y-4">
    {#each thread.messages as message (message.id)}
      {@const isGuest = message.sender_type === 'guest'}
      <div class="flex" class:justify-end={!isGuest}>
        <div class="max-w-[75%]">
          <div class="p-card p-3"
               style={isGuest ? '' : 'background-color: var(--p-brand-light)'}>
            <p class="whitespace-pre-wrap text-sm">{message.body}</p>
          </div>
          <p class="mt-1 text-xs p-faint" class:text-right={!isGuest}>
            {message.sender_name ?? message.sender_type ?? ''} · {fmt(message.sent_at)}
          </p>
        </div>
      </div>

      {#each responsesByMessage.get(message.id) ?? [] as response (response.id)}
        <div class="ml-6 border-l-2 pl-4" style="border-color: var(--p-border-strong)">
          <p class="text-xs font-medium" style="color: {outcomeTone(response)}">
            {outcomeLabel(response)}
            {#if response.confidence_score != null}
              · confidence {Math.round(response.confidence_score * 100)}%
            {/if}
            {#if response.trigger_reason}
              · {response.trigger_reason.replace('_', ' ')}
            {/if}
          </p>
          {#if response.response_text}
            <p class="mt-1 whitespace-pre-wrap text-sm p-muted">{response.response_text}</p>
          {:else}
            <p class="mt-1 text-sm p-faint">
              No draft was generated — this needed a person to write the reply.
            </p>
          {/if}
        </div>
      {/each}
    {/each}
  </div>
{/if}
